import { useState, useRef, ReactNode, MouseEvent } from 'react';
import cn from 'classnames';
import { useClickAway } from 'react-use';
import { createPortal } from 'react-dom';

import { Button } from './Button';
import styles from './DropdownButton.module.scss';

interface IDropdownItem {
  id: string | number;
  content?: string | ReactNode;
  isDivider?: boolean;
  additionalContent?: IDropdownItem[];
}

interface IDropdownButtonProps {
  items: IDropdownItem[];
  visible?: boolean;
  disabled?: boolean;
  onVisibleChange?: (value: boolean) => void;
}

const DropdownButton = ({ items, visible, disabled, onVisibleChange }: IDropdownButtonProps) => {
  const body = document.querySelector('body');
  const [isVisible, setIsVisible] = useState(false);
  const [location, setLocation] = useState<{ top: number; right: number } | undefined>(undefined);
  const mainRef = useRef(null);
  const menuRef = useRef(null);

  useClickAway<Event & { path?: string[] }>(menuRef, e => {
    const currentPath = e?.path;
    if (isVisible && !currentPath?.find(item => item === mainRef.current)) {
      setIsVisible(false);
    }
  });

  const handleVisibleChange = (e: MouseEvent<HTMLElement>) => {
    setIsVisible(!isVisible);
    if (onVisibleChange) {
      onVisibleChange(!isVisible);
    }
    if (!isVisible) {
      const elem = e.currentTarget;
      if (elem && body) {
        const { top, height, x, width } = elem.getBoundingClientRect();
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        setLocation({
          top: top + height + window.scrollY + 5,
          right: window.innerWidth - x - scrollBarWidth - width,
        });
      }
    }
  };

  const hideMenu = () => {
    setIsVisible(false);
  };

  return (
    <div className={styles.Dropdown} ref={mainRef}>
      <Button
        className={cn(styles.DropdownButton, {
          [styles.DropdownButtonActive]: isVisible && visible !== false,
        })}
        text="Действия"
        id="visibleChangeActionsButton"
        type="button"
        endArrow
        disabled={disabled}
        onClick={handleVisibleChange}
      />
      {isVisible &&
        body &&
        createPortal(
          <ul
            className={cn(styles.Menu, { [styles.MenuIsActive]: isVisible && visible !== false })}
            style={{ top: location?.top, right: location?.right }}
            ref={menuRef}
            id="modal"
          >
            {items.map(item => {
              if (item.isDivider) {
                return <li key={item.id} className={styles.MenuDivider} />;
              }
              return (
                <li className={styles.ListItem} key={item.id} onClick={hideMenu}>
                  {item.content}
                  {item.additionalContent && (
                    <ul className={styles.AdditionalMenu}>
                      {item.additionalContent.map((additional: IDropdownItem) => (
                        <li key={additional.id}>{additional.content}</li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>,
          body,
        )}
    </div>
  );
};

export { DropdownButton };
